import { createContext, type ReactNode, useContext, useMemo } from "react";
import { useSessionStorage } from "usehooks-ts";
import {
  AccountApi,
  AnnouncementApi,
  CaseApi,
  CaseFileApi,
  ClientApi,
  CommunicationApi,
  Configuration,
  ContentApi,
  CredentialApi,
  DefinitionApi,
  DocumentAdminApi,
  DocumentApi,
  FirmApi,
  FormApi,
  ObjectionApi,
  ProductionSetApi,
  ReportsApi,
  StripeApi,
  TagApi,
  UserApi,
} from "@/briefpoint-client";
import { JurisdictionApi } from "@/briefpoint-client/apis/JurisdictionApi";
import Loading from "@/components/Loading";
import { OnBehalfOfHeaderKey, OnBehalfOfSessionKey } from "@/hooks/useAuth";
import { useProvideOAuth } from "@/hooks/useOAuth";

interface ApiContextType {
  accountApi: AccountApi;
  announcementApi: AnnouncementApi;
  caseApi: CaseApi;
  caseFileApi: CaseFileApi;
  clientApi: ClientApi;
  communicationApi: CommunicationApi;
  contentApi: ContentApi;
  credentialApi: CredentialApi;
  definitionsApi: DefinitionApi;
  documentAdminApi: DocumentAdminApi;
  documentApi: DocumentApi;
  firmApi: FirmApi;
  formApi: FormApi;
  objectionApi: ObjectionApi;
  reportsApi: ReportsApi;
  stripeApi: StripeApi;
  tagApi: TagApi;
  userApi: UserApi;
  jurisdictionApi: JurisdictionApi;
  productionSetApi: ProductionSetApi;
}

const ApiContext = createContext<ApiContextType | null>(null);

export function ApiProvider({ children }: { children: ReactNode }) {
  const { getToken } = useProvideOAuth();
  const basePath = import.meta.env.VITE_API_URL;
  const [onbehalfof] = useSessionStorage(OnBehalfOfSessionKey, undefined);

  const apiInstances = useMemo(() => {
    // If getToken is not available yet, return null to prevent errors
    if (!getToken) {
      return null;
    }

    const headers = onbehalfof
      ? { [OnBehalfOfHeaderKey]: onbehalfof }
      : undefined;

    const config = new Configuration({
      basePath,
      accessToken: getToken,
      headers,
    });

    return {
      accountApi: new AccountApi(config),
      announcementApi: new AnnouncementApi(config),
      caseApi: new CaseApi(config),
      caseFileApi: new CaseFileApi(config),
      clientApi: new ClientApi(config),
      communicationApi: new CommunicationApi(config),
      contentApi: new ContentApi(config),
      credentialApi: new CredentialApi(config),
      definitionsApi: new DefinitionApi(config),
      documentAdminApi: new DocumentAdminApi(config),
      documentApi: new DocumentApi(config),
      firmApi: new FirmApi(config),
      formApi: new FormApi(config),
      objectionApi: new ObjectionApi(config),
      reportsApi: new ReportsApi(config),
      stripeApi: new StripeApi(config),
      tagApi: new TagApi(config),
      userApi: new UserApi(config),
      jurisdictionApi: new JurisdictionApi(config),
      productionSetApi: new ProductionSetApi(config),
    };
  }, [onbehalfof, getToken]);

  // If API instances are not ready yet, show loading
  if (!apiInstances) {
    return <Loading isLoading={true} />;
  }

  return (
    <ApiContext.Provider value={apiInstances}>{children}</ApiContext.Provider>
  );
}

export function useApiContext() {
  const context = useContext(ApiContext);
  return context;
}
